import { useMemo } from 'react';
import { useHistory } from 'react-router';
import {
  Chatbot,
  ChatbotChatHistory,
  ChatbotChatMessage,
  ChatRole,
  CrisisState,
  CrisisType,
  useChatbots,
} from '../../hooks/useChatBot';
import { convertObjectToParams } from '../../components/common/create-params';
import { ROUTE_PATH } from '../../routes/paths';

interface ChatbotMessagesTableProps {
  chatbotMessages: ChatbotChatHistory[];
  onClearSelectedChat?: () => void;
  selectedChatId?: number | null;
}

interface GroupedMessage extends ChatbotChatMessage {
  chatId: number;
  displayName: string;
  memberLabel: string;
  userId?: number;
  callerRoleId?: number | null;
  crisis_state: CrisisState;
  crisis_type: CrisisType;
}

const ChatbotMessagesTable: React.FC<ChatbotMessagesTableProps> = ({
  chatbotMessages,
  onClearSelectedChat,
  selectedChatId,
}) => {
  const history = useHistory();

  const getGroupedMessages = (messages: ChatbotChatHistory[], chatbots: Chatbot[] = []): GroupedMessage[] => {
    return messages
      .flatMap((chat) => {
        const displayName = chatbots.find((chatbot: Chatbot) => chatbot.id === chat.chatbot_id)?.display_name ?? '';
        const memberLabel = chat.user_display_name?.trim() || (chat.user_id != null ? `User ${chat.user_id}` : '—');

        return chat.messages.map((message) => ({
          ...message,
          chatId: chat.id,
          displayName,
          memberLabel,
          userId: chat.user_id,
          callerRoleId: chat.caller_role_id,
          crisis_state: chat.crisis_state,
          crisis_type: chat.crisis_type,
        }));
      })
      .filter((message) => !(message.role === ChatRole.SYSTEM && message.content === null));
  };

  const { data: allChatbots } = useChatbots();

  const groupedMessages = useMemo(
    () => getGroupedMessages(chatbotMessages, allChatbots?.chatbots),
    [chatbotMessages, allChatbots],
  );

  const openMember = (userId?: number, callerRoleId?: number | null, memberLabel?: string) => {
    if (userId == null || callerRoleId == null) return;
    history.push(
      ROUTE_PATH.MEMBERS_PROFILE +
        convertObjectToParams({
          name: memberLabel || String(userId),
          id: userId,
          callerRoleId,
        }),
    );
  };

  return (
    <div className="max-w-7xl overflow-x-auto p-4">
      <div className="mb-3 flex items-center justify-between">
        {selectedChatId !== null && selectedChatId !== undefined ? (
          <span className="text-sm text-gray-700">
            Showing chat history for chat <span className="font-semibold">{selectedChatId}</span>
          </span>
        ) : (
          <span className="text-sm text-gray-500">Showing chat history for all chats</span>
        )}

        {onClearSelectedChat && (
          <button
            type="button"
            onClick={onClearSelectedChat}
            className="rounded border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Clear chat filter
          </button>
        )}
      </div>

      {groupedMessages?.length === 0 && (
        <p className="text-sm text-gray-500">No chat messages available for the selected filters.</p>
      )}

      {groupedMessages?.length > 0 && (
        <table className="min-w-full table-fixed border-collapse">
          <caption className="mb-4 text-left">Peersona Chat Logs</caption>
          <thead>
            <tr className="bg-gray-100">
              <th scope="col" className="border p-2">
                Display Name
              </th>
              <th scope="col" className="w-32 border p-2">
                Chat ID
              </th>
              <th scope="col" className="w-48 border p-2">
                Member
              </th>
              <th scope="col" className="w-40 border p-2">
                Role
              </th>
              <th scope="col" className="border p-2">
                Message
              </th>
              <th scope="col" className="w-32 border p-2">
                Crisis State
              </th>
              <th scope="col" className="w-40 border p-2">
                Crisis Type
              </th>
              <th scope="col" className="border p-2">
                Time
              </th>
            </tr>
          </thead>
          <tbody>
            {groupedMessages.map((message, index) => {
              const timestamp = new Date(message.timestamp);
              const formattedTimestamp = Number.isNaN(timestamp.getTime())
                ? `${message.timestamp ?? ''}`
                : timestamp.toLocaleString();
              const canOpenMember = message.userId != null && message.callerRoleId != null;

              return (
                <tr
                  key={`${message.chatId}-${message.role}-${index}`}
                  className={message.role === ChatRole.ASSISTANT ? 'bg-gray-100' : ''}
                >
                  <td className="border p-2 font-bold text-gray-700">{message.displayName}</td>
                  <td className="border p-2 font-bold text-gray-700">{message.chatId}</td>
                  <td className="border p-2 font-bold text-gray-700">
                    {canOpenMember ? (
                      <button
                        type="button"
                        className="text-left text-blue-700 underline hover:text-blue-900"
                        onClick={() => openMember(message.userId, message.callerRoleId, message.memberLabel)}
                      >
                        {message.memberLabel}
                        <span className="ml-1 font-normal text-gray-500">#{message.userId}</span>
                      </button>
                    ) : (
                      message.memberLabel
                    )}
                  </td>
                  <td className="border p-2 font-bold capitalize text-gray-700">{message.role}</td>
                  <td className="border p-2 text-gray-700">{message.content}</td>
                  <td className="border p-2 font-bold text-gray-700">{message.crisis_state}</td>
                  <td className="border p-2 font-bold text-gray-700">{message.crisis_type}</td>
                  <td className="border p-2 font-bold text-gray-700">{formattedTimestamp}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ChatbotMessagesTable;
